import type { JSX } from 'react';
import { DateTime } from '@ksyos/date-time';
import { FormSection, Radio } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { BariatricsExternalReferralData } from '@common/validation/customControls/bariatrics/bariatricsExternalReferral/v1/bariatricsExternalReferralSchema';
import type { BariPostNurseSpecialistFollowUpData } from '@common/validation/customControls/bariPost/bariPostNurseSpecialistFollowUp/types';
import type { BariPostTreatmentReportData } from '@common/validation/customControls/bariPost/bariPostTreatmentReport/v1/bariPostTreatmentReportSchemaV1';
import type { IOption } from '@frontend/components/interfaces';
import { StatusBehandeling } from '@common/customControls/bariatrics';
import { CustomControlCode } from '@common/enums/serviceWorkflow/customControlCode';
import { doorverwijzingOptionExtern } from '@common/validation/customControls/bariPost/bariPostTreatmentReport/v1/bariPostTreatmentReportSchemaV1';
import { ProcessContext } from '@frontend/components/pages/episodeCategory/carePath/process/processContext';
import { getRemainingDoorverwijzingenOptions } from '@frontend/components/pages/episodeCategory/customControls/bariPost/bariPostTreatmentReport/bariPostTreatmentReportUtils';
import { getProcessActivities } from '@frontend/services/api/processesApiService';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useDefinedContext } from '@frontend/utils/hooks/useDefinedContext';
import { useProcessActivityEditContext } from '@frontend/utils/hooks/useProcessActivityContext';
import { ProcessActivityAssetsDropzone } from '../../processActivityAssets/ProcessActivityAssetsDropzone';
import {
    DateInputFormGroup,
    RadioGroupFormGroup,
    SelectFormGroup,
    TextAreaFormGroup,
} from '../../processActivityFormGroupFields';

const behandelingStatussen: StatusBehandeling[] = [StatusBehandeling.nog_bezig, StatusBehandeling.afgerond];

export function BariPostTreatmentReportEdit(): JSX.Element {
    const abortSignal = useAbortSignal();
    const [doorverwijzingenOptions, setDoorverwijzingenOptions] = useState<IOption<string>[]>([]);
    const { data, errors, onChange, processActivityId } = useProcessActivityEditContext<BariPostTreatmentReportData>();
    const { process } = useDefinedContext(ProcessContext);

    useEffect(() => {
        loadDoorverwijzingenOptions(process.id);
    }, [process.id]);

    useEffect(() => {
        if (!processActivityId && doorverwijzingenOptions.length > 0 && doorverwijzingenOptions.length <= 2) {
            handleChange(doorverwijzingenOptions[doorverwijzingenOptions.length - 1].value);
        }
    }, [processActivityId, doorverwijzingenOptions.length]);

    return (
        <FormSection>
            <SelectFormGroup
                error={errors.get('doorverwijzing')}
                fieldKey="relatedProcessActivityId"
                isRequired={true}
                label="Doorverwijzing"
                onChange={handleChange}
                options={doorverwijzingenOptions}
                placeholder="Selecteer doorverwijzing"
                value={
                    data.doorverwijzing === doorverwijzingOptionExtern
                        ? doorverwijzingOptionExtern
                        : data.relatedProcessActivityId
                }
            />
            <ProcessActivityAssetsDropzone
                errorText="Voeg alsjeblieft het bestand met het verslag toe"
                isRequired={true}
                label="Verslag"
            />
            <DateInputFormGroup
                fieldKey="datum_afgerond"
                isRequired={true}
                label="Datum afgerond"
                maxDate={DateTime.now().format()}
            />
            <RadioGroupFormGroup
                fieldKey="status_behandeling"
                isRequired={true}
                label="Status behandeling"
                onChange={async (value) =>
                    onChange(
                        {
                            status_behandeling: value as StatusBehandeling,
                        },
                        'status_behandeling'
                    )
                }
            >
                {behandelingStatussen.map((value) => (
                    <Radio
                        key={value}
                        label={value}
                        value={value}
                    />
                ))}
            </RadioGroupFormGroup>
            <TextAreaFormGroup
                fieldKey="toelichting"
                label="Toelichting"
            />
        </FormSection>
    );

    async function loadDoorverwijzingenOptions(processId: string): Promise<void> {
        const defaultOption = {
            label: doorverwijzingOptionExtern,
            value: doorverwijzingOptionExtern,
        };

        const { items: referralActivities } = await getProcessActivities<BariatricsExternalReferralData>(
            processId,
            {
                customControlCode: CustomControlCode.BariPostNurseSpecialistReferral,
            },
            abortSignal
        );
        const { items: followUpActivities } = await getProcessActivities<BariPostNurseSpecialistFollowUpData>(
            processId,
            { customControlCode: CustomControlCode.BariPostNurseSpecialistFollowUp },
            abortSignal
        );
        const { items: treatmentReportActivities } = await getProcessActivities<BariPostTreatmentReportData>(
            processId,
            { customControlCode: CustomControlCode.BariPostTreatmentReport },
            abortSignal
        );

        setDoorverwijzingenOptions([
            defaultOption,
            ...getRemainingDoorverwijzingenOptions(referralActivities, followUpActivities, treatmentReportActivities),
        ]);
    }

    function handleChange(value: string | null) {
        const doorverwijzing = doorverwijzingenOptions.find((option) => option.value === value);
        onChange(
            {
                doorverwijzing: doorverwijzing?.label || null,
                relatedProcessActivityId:
                    !doorverwijzing || doorverwijzing.label === doorverwijzingOptionExtern
                        ? null
                        : doorverwijzing.value,
            },
            ['doorverwijzing', 'relatedProcessActivityId']
        );
    }
}
